import React, { forwardRef, useEffect, useId, useRef } from 'react';
import * as styles from './Checkbox.module.css';
import type { CheckboxProps } from './types';

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      description,
      error,
      size = 'md',
      fullWidth = false,
      indeterminate = false,
      className = '',
      labelClassName = '',
      controlClassName = '',
      id,
      disabled,
      checked,
      defaultChecked,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      onChange,
      onCheckedChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const descriptionId = description ? `${inputId}-description` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId]
      .filter(Boolean)
      .join(' ');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const s = (styles as Record<string, string>) || {};

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate, checked]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = event => {
      onCheckedChange?.(event.target.checked);
      onChange?.(event);
    };

    const handleRef = (node: HTMLInputElement | null) => {
      inputRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
        return;
      }

      if (ref) {
        ref.current = node;
      }
    };

    const rootClasses = [
      s.root || '',
      s[size] || '',
      fullWidth ? s.fullWidth || '' : '',
      disabled ? s.disabled || '' : '',
      error ? s.invalid || '' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const controlClasses = [s.control || '', controlClassName]
      .filter(Boolean)
      .join(' ');

    const labelClasses = [s.label || '', labelClassName]
      .filter(Boolean)
      .join(' ');

    return (
      <label className={rootClasses} htmlFor={inputId}>
        <span className={controlClasses}>
          <input
            {...props}
            ref={handleRef}
            id={inputId}
            type="checkbox"
            className={s.input || ''}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            aria-describedby={describedBy || undefined}
            aria-invalid={error ? true : ariaInvalid}
            onChange={handleChange}
          />
          <span className={s.box || ''} aria-hidden="true">
            <svg
              className={s.checkIcon || ''}
              viewBox="0 0 16 16"
              fill="none"
              focusable="false"
            >
              <path
                d="M3.5 8.2 6.6 11.3 12.8 4.7"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className={s.indeterminateIcon || ''} />
          </span>
        </span>

        {(label || description || error) && (
          <span className={s.content || ''}>
            {label && <span className={labelClasses}>{label}</span>}
            {description && (
              <span id={descriptionId} className={s.description || ''}>
                {description}
              </span>
            )}
            {error && (
              <span id={errorId} className={s.error || ''}>
                {error}
              </span>
            )}
          </span>
        )}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
export type { CheckboxProps, CheckboxSize } from './types';
